/**
 * ShoppingModeView — the Shopping-mode (in-store) surface of the active list
 * (T12.1.5.1.7). Built up from the legacy flat checklist to the prototype's
 * `#s-list-shop`:
 *
 *   - Pending items grouped by selected store (the item's `display_store` /
 *     `store_name`, set only when the household picked a store/offer in
 *     Planning). Items with no known store fall into a single flat "Your list"
 *     group — store/aisle assignments are NEVER fabricated (no aisle data
 *     exists, so there is no aisle grouping; that is out of scope).
 *   - A store header per group with an always-visible remaining count.
 *   - Fat single-tap completion rows (44+ row, 26 box). One tap anywhere on
 *     the row toggles the item between pending and purchased — through the same
 *     orchestrator path the legacy list used (so the T6 reducer → storage → API
 *     contract stays intact).
 *   - Completed items HIDDEN by default behind a "Show purchased (N)" toggle,
 *     with the remaining count always visible (per-group + page-level).
 *   - One passive nudge slot (static placeholder).
 *
 * Bounded to the prototype's thin depth: no scan/price-check, store-progress
 * nodes, loyalty-at-till, next-stop, or aisle reordering.
 *
 * One mobile-first responsive surface; desktop is the same app, widened/centered
 * by the shell wrapper. All styles flow from tokens (CP5).
 */

import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import {
  useActiveShoppingList,
  useHouseholdProducts,
  useSelectedStores,
  useStorePreferences,
  useStores,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import {
  formatResolvedMeasurement,
  type ShoppingListItemWithDetails,
} from '@shopista/shared/types';
import { Card } from '../../components';
import { useOrchestrators } from '../../stores/orchestrator-context';
import { useBadgeSignals } from '../deals/useBadgeSignals';
import { usePreferenceBroadcastSync } from '../../lib/preferenceBroadcast';
import { shoppingListItemThumb } from './shoppingListItemThumb';
import { addedAtRelative } from './addedAtByline';
import {
  type StoreDealEntry,
  type StoreLocationOffers,
} from './storeLocationOfferResolver';
import { StoreLocationOffersLoader } from './StoreLocationOffersLoader';
import { WhereToBuyPicker } from './WhereToBuyPicker';
import { DealOverlay } from '../deals/DealOverlay';
import {
  groupByStore,
  storeOf,
  UNKNOWN_STORE_LABEL,
  type StoreGroup,
} from './groupByStore';
import { openReportProblemSheet } from '../report-problems';
import * as planStyles from './planningModeStyles';
import * as s from './shoppingModeStyles';

// Stable empty set for items with no linked SystemProduct id yet (avoids a new
// Set() per render that would churn the picker's match memo).
const EMPTY_IDS: ReadonlySet<string> = new Set();

// T41 — the DOM id of a store group's items container. `aria-controls` on the
// header and the switcher's `scrollIntoView` both target it. Derived from the
// stable group key (the store-location id, or the unassigned bucket key).
function storeGroupDomId(groupKey: string): string {
  return `shop-group-items-${groupKey}`;
}

// T41 — a short branch/street locator for a store header (prototype `.loc`,
// e.g. "Bank St" from "350 Bank St, Ottawa"). Resolved from the `StoreLocation`
// row's `address` (name + address fields only — the hours fields ride unused
// until F23). Takes the first comma-delimited segment (the street line) so two
// same-chain locations are visually distinct; returns null when there's no
// usable address so the header shows the name alone.
function branchLabelOf(address: string | null | undefined): string | null {
  if (address == null) return null;
  const firstSegment = address.split(',')[0]?.trim() ?? '';
  return firstSegment !== '' ? firstSegment : null;
}

// T35.3 — the all-done feedback touch-point row. Token-driven (CP5); ≥44px tap.
const allDoneFeedbackStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: '2.75rem',
  marginTop: 'var(--space-2)',
  padding: 'var(--space-3)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-secondary)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  textAlign: 'left',
  cursor: 'pointer',
};

const allDoneFeedbackArrowStyle: CSSProperties = {
  marginLeft: 'auto',
  color: 'var(--color-text-muted)',
  flex: '0 0 auto',
};

export function ShoppingModeView(): JSX.Element {
  const listQuery = useActiveShoppingList();
  const orchestrators = useOrchestrators();
  const [showPurchased, setShowPurchased] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // T27.2.7.9.3 — the Where picker is shared with Planning, so Shopping reads the
  // same context: the household's selected stores (what the picker lists), the
  // preferred store (pre-highlight), the brand/badge signals (deal qualifiers),
  // and each product's linked SystemProduct ids (identity match).
  const selectedStoresQuery = useSelectedStores();
  const storesQuery = useStores();
  const storePreferencesQuery = useStorePreferences();
  const productsQuery = useHouseholdProducts();
  usePreferenceBroadcastSync();
  const badgeSignals = useBadgeSignals();
  const likedBrandNames = useMemo(() => {
    const set = new Set(badgeSignals.likedBrandNames);
    for (const n of badgeSignals.preferredBrandNames) set.add(n);
    return set;
  }, [badgeSignals]);
  const productSystemProductIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of productsQuery.data?.products ?? []) {
      map.set(p.id, new Set(p.linked_systemproduct_ids ?? []));
    }
    return map;
  }, [productsQuery.data]);

  const selectedStores = useMemo(() => {
    const ids = selectedStoresQuery.data?.storeIds ?? [];
    const byId = new Map(
      (storesQuery.data?.stores ?? []).map((st) => [st.id, st.name]),
    );
    return ids.map((id) => ({ id, name: byId.get(id) ?? null }));
  }, [selectedStoresQuery.data, storesQuery.data]);

  const storesResolved =
    !selectedStoresQuery.isLoading && !storesQuery.isLoading;

  const preferredStoreId = useMemo(() => {
    const fav = (storePreferencesQuery.data?.selected ?? []).find(
      (entry) => entry.is_favorite,
    );
    return fav?.storelocation_id ?? null;
  }, [storePreferencesQuery.data]);

  const items = listQuery.data?.items ?? [];
  const pendingItems = useMemo(
    () => items.filter((it) => it.status === 'pending'),
    [items],
  );
  const purchasedItems = useMemo(
    () => items.filter((it) => it.status === 'purchased'),
    [items],
  );
  const groups = useMemo(() => groupByStore(pendingItems), [pendingItems]);

  // T41 fix — labels shared by ≥2 groups. The branch only disambiguates when two
  // groups read the same store name; for a unique name the branch is redundant
  // noise on the chip, so it renders only for a colliding label.
  const duplicateLabels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const g of groups) counts.set(g.label, (counts.get(g.label) ?? 0) + 1);
    return new Set(
      [...counts.entries()].filter(([, n]) => n > 1).map(([l]) => l),
    );
  }, [groups]);

  // T41 — per-group collapse state. A `Set<string>` of collapsed group keys;
  // default expanded (empty set). Collapsing one store never affects another.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // T41 — the store-location rows, keyed by id, so each group resolves its
  // `StoreLocation` for the branch label + switcher chip via `group.storeId`.
  // Reads name + address only; the hours fields ride unused until F23.
  const storesById = useMemo(() => {
    const map = new Map<string, { name: string; address: string | null }>();
    for (const st of storesQuery.data?.stores ?? []) {
      map.set(st.id, { name: st.name, address: st.address ?? null });
    }
    return map;
  }, [storesQuery.data]);

  // T41 — resolve a group's short branch label (null for the unassigned bucket
  // or a store with no usable address). Matched on the stable `storeId`.
  const branchOf = useCallback(
    (group: StoreGroup): string | null => {
      if (group.storeId == null) return null;
      return branchLabelOf(storesById.get(group.storeId)?.address);
    },
    [storesById],
  );

  // T41 — the sticky switcher jump: collapse every sibling, expand the target,
  // and scroll its items container into view. Matched on the stable group key
  // (the store-location id / unassigned bucket key), not the visible label.
  const jumpToGroup = useCallback(
    (targetKey: string) => {
      setCollapsed(new Set(groups.map((g) => g.key).filter((k) => k !== targetKey)));
      const el = document.getElementById(storeGroupDomId(targetKey));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [groups],
  );

  // Single-tap completion: pending → purchased (and back when re-tapped from the
  // purchased section). Reuses the legacy orchestrator path.
  const handleToggle = async (item: ShoppingListItemWithDetails) => {
    setActionError(null);
    const nextChecked = !item.checked;
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: {
          checked: nextChecked,
          status: nextChecked ? 'purchased' : 'pending',
        },
        request: {
          checked: nextChecked,
          status: nextChecked ? 'purchased' : 'pending',
        },
      });
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Failed to update item'));
    }
  };

  // Quantity quick tap-edit (`qtyedit`) from the expanded view — same
  // orchestrator path the Planning stepper uses, so the value persists for the
  // trip and reads back the same across lists.
  const handleSetQuantity = async (
    item: ShoppingListItemWithDetails,
    quantity: number,
  ) => {
    setActionError(null);
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: { quantity },
        request: { quantity },
      });
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Failed to update quantity'));
    }
  };

  // T27.2.7.9.3 — the where-to-buy store pick, shared with Planning. Persists
  // `selectedStoreLocationId` (always) + `selectedRetailerOfferId` (the matched
  // offer when the chosen store has a deal, else null). Re-tap → `(null, null)`
  // clears both. Same orchestrator path; the `/active` refetch (kept for store
  // picks) lands the chosen store name so the row regroups in-store.
  //
  // T27.2.7.9.6 (reopen fix 2) — a bare store pick ALWAYS clears the pin,
  // unconditionally, for BOTH deselect and switch-to-a-different-store. A flyer
  // belongs to its retailer, so a Walmart flyer can never apply to an Atlantic
  // selection. (A flyer is re-pinned through `handlePinDeal`, which sets the
  // store + pin together.)
  const handlePickStore = useCallback(
    async (
      item: ShoppingListItemWithDetails,
      storeLocationId: string | null,
      offerId: string | null,
    ) => {
      setActionError(null);
      try {
        await orchestrators.ShoppingLists.handleUpdateItem({
          listId: item.shoppinglist_id,
          itemId: item.id,
          changes: {
            selected_storelocation_id: storeLocationId,
            selected_retaileroffer_id: offerId,
            flyerdeal_id: null,
            flyerdeal_valid_from: null,
            flyerdeal_valid_to: null,
          },
          request: {
            selectedStoreLocationId: storeLocationId,
            selectedRetailerOfferId: offerId,
            flyerDealId: null,
            flyerDealValidFrom: null,
            flyerDealValidTo: null,
          },
        });
      } catch (err) {
        setActionError(apiErrorMessage(err, 'Failed to save store pick'));
      }
    },
    [orchestrators],
  );

  // T27.2.7.9.6 — pin a flyer to the item (or clear it). PINNING sets the chosen
  // store + matched offer + the pin (`flyerdeal_id` + validity) in ONE coherent
  // write — a flyer belongs to its store, so pinning a Walmart deal moves the
  // chosen store to Walmart (never leaves an Atlantic pick with a Walmart pin). A
  // set pin drives the headline price + Trip grouping (resolved server-side on the
  // `/active` refetch). UNPINNING (`deal === null`) clears the pin only and KEEPS
  // the store. `retailerOfferId` is the deal's store's matched offer id, threaded
  // from the picker (or null). Shared with Planning.
  const handlePinDeal = useCallback(
    async (
      item: ShoppingListItemWithDetails,
      deal: StoreDealEntry | null,
      retailerOfferId: string | null,
    ) => {
      setActionError(null);
      const storeChange =
        deal != null
          ? {
              selected_storelocation_id: deal.storelocation_id,
              selected_retaileroffer_id: retailerOfferId,
            }
          : {};
      const storeChangeReq =
        deal != null
          ? {
              selectedStoreLocationId: deal.storelocation_id,
              selectedRetailerOfferId: retailerOfferId,
            }
          : {};
      try {
        await orchestrators.ShoppingLists.handleUpdateItem({
          listId: item.shoppinglist_id,
          itemId: item.id,
          changes: {
            ...storeChange,
            flyerdeal_id: deal?.flyerdeal_id ?? null,
            flyerdeal_valid_from: deal?.valid_from ?? null,
            flyerdeal_valid_to: deal?.valid_to ?? null,
          },
          request: {
            ...storeChangeReq,
            flyerDealId: deal?.flyerdeal_id ?? null,
            flyerDealValidFrom: deal?.valid_from ?? null,
            flyerDealValidTo: deal?.valid_to ?? null,
          },
        });
      } catch (err) {
        setActionError(apiErrorMessage(err, 'Failed to pin deal'));
      }
    },
    [orchestrators],
  );

  // T27.2.7.9.6 — "Buy regular here": clear the pinned flyer (`flyerdeal_id` +
  // snapshot) AND the carried standard offer (`selected_retaileroffer_id`) while
  // KEEPING the selected store, so the compact row prices nothing (server-side).
  // Distinct from re-tapping the store, which deselects the store entirely.
  const handleRemoveOffer = useCallback(
    async (item: ShoppingListItemWithDetails) => {
      setActionError(null);
      try {
        await orchestrators.ShoppingLists.handleUpdateItem({
          listId: item.shoppinglist_id,
          itemId: item.id,
          changes: {
            selected_retaileroffer_id: null,
            flyerdeal_id: null,
            flyerdeal_valid_from: null,
            flyerdeal_valid_to: null,
          },
          request: {
            selectedRetailerOfferId: null,
            flyerDealId: null,
            flyerDealValidFrom: null,
            flyerDealValidTo: null,
          },
        });
      } catch (err) {
        setActionError(apiErrorMessage(err, 'Failed to remove offer'));
      }
    },
    [orchestrators],
  );

  // Mark-unavailable: a pending item the shelf is out of moves to `deferred` —
  // the existing third status, no schema change. A deferred item leaves the
  // pending set (pendingItems filters status === 'pending') so it drops out of
  // the in-store list without being marked purchased. Same orchestrator path.
  const handleMarkUnavailable = async (item: ShoppingListItemWithDetails) => {
    setActionError(null);
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: { status: 'deferred' },
        request: { status: 'deferred' },
      });
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Failed to update item'));
    }
  };

  if (listQuery.isError) {
    return (
      <Card padding="lg" data-testid="shop-error">
        <p>Could not load your list.</p>
        <p style={s.errorStyle}>
          {listQuery.error instanceof Error
            ? listQuery.error.message
            : String(listQuery.error)}
        </p>
      </Card>
    );
  }

  const remaining = pendingItems.length;

  return (
    <div style={s.pageWrapperStyle} data-testid="shop-page" data-mode="shopping">
      <h2 style={s.titleStyle}>Shopping</h2>
      <p style={s.subStyle} data-testid="shop-remaining">
        {remaining === 0
          ? 'All done — nothing left to grab.'
          : `${remaining} ${remaining === 1 ? 'item' : 'items'} left`}
      </p>

      {pendingItems.length === 0 && purchasedItems.length === 0 ? (
        <Card padding="lg" data-testid="shop-empty">
          <div style={s.emptyCardStyle}>
            <div style={s.emptyEmojiStyle} aria-hidden="true">
              🛒
            </div>
            <p>Your list is empty.</p>
            <p>Add items in Planning, then switch here to shop.</p>
          </div>
        </Card>
      ) : (
        <>
          {/* One passive nudge slot — static placeholder, calm by default. */}
          {remaining > 0 && (
            <div style={s.nudgeStyle} data-testid="shop-nudge">
              <span style={s.nudgeIconStyle} aria-hidden="true">
                🏷️
              </span>
              <span style={s.nudgeTextStyle}>
                Tap an item as you grab it — completed items tuck away so the
                list stays short.
              </span>
            </div>
          )}

          {remaining === 0 ? (
            <>
              <div style={s.allDoneStyle} data-testid="shop-all-done">
                Everything&apos;s in the cart. 🎉
              </div>
              {/* T35.3 / F21 — the all-done shopping touch-point. It surfaces
                  ONLY when the trip is finished (nothing left to grab), so it
                  never interrupts the in-store flow; tapping it opens the shared
                  report sheet (pre-set to "other", carrying the shopping
                  screen). Ambient by intent. */}
              <button
                type="button"
                style={allDoneFeedbackStyle}
                onClick={() =>
                  openReportProblemSheet({ screenContext: 'Shopping (just finished a trip)' })
                }
                data-testid="shop-all-done-feedback"
              >
                <span aria-hidden="true">💬</span>
                <span>How did this trip go? Tell us anything.</span>
                <span style={allDoneFeedbackArrowStyle} aria-hidden="true">
                  ›
                </span>
              </button>
            </>
          ) : (
            <>
              {/* T41 — sticky store switcher: one chip per group (incl. "Grab
                  anywhere"). Tapping a chip collapses siblings, expands the
                  target, and scrolls it into view. Plain chips — no status color
                  (deferred to F23). Rendered only when there's more than one
                  group, so a single-store trip stays uncluttered. */}
              {groups.length > 1 && (
                <div style={s.storeSwitchStyle} data-testid="shop-store-switch">
                  {groups.map((group) => {
                    const branch = branchOf(group);
                    return (
                      <button
                        type="button"
                        key={group.key}
                        style={s.storeSwitchChipStyle}
                        onClick={() => jumpToGroup(group.key)}
                        data-testid={`shop-switch-${group.key}`}
                      >
                        <span style={s.storeSwitchChipNameStyle}>
                          {group.label}
                        </span>
                        {branch && duplicateLabels.has(group.label) && (
                          <span style={s.storeSwitchChipBranchStyle}>
                            {branch}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {groups.map((group) => {
                const isCollapsed = collapsed.has(group.key);
                const itemsId = storeGroupDomId(group.key);
                const branch = branchOf(group);
                return (
                  <div
                    key={group.key}
                    id={itemsId}
                    style={s.storeGroupStyle}
                    data-testid={`shop-group-${group.key}`}
                  >
                    <button
                      type="button"
                      style={s.storeHeadStyle}
                      onClick={() => toggleGroup(group.key)}
                      aria-expanded={!isCollapsed}
                      aria-controls={itemsId}
                      data-testid={`shop-group-${group.key}-head`}
                    >
                      <span style={s.storeHeadLabelGroupStyle}>
                        <span style={s.storeHeadNameStyle}>{group.label}</span>
                        {branch && (
                          <span
                            style={s.storeHeadBranchStyle}
                            data-testid={`shop-group-${group.key}-branch`}
                          >
                            {branch}
                          </span>
                        )}
                      </span>
                      <span
                        style={s.storeHeadCountStyle}
                        data-testid={`shop-group-${group.key}-count`}
                      >
                        {group.items.length}{' '}
                        {group.items.length === 1 ? 'item' : 'items'} left
                      </span>
                      <span
                        style={
                          isCollapsed
                            ? s.storeHeadChevronCollapsedStyle
                            : s.storeHeadChevronStyle
                        }
                        aria-hidden="true"
                      >
                        ⌄
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div style={s.storeItemsStyle}>
                        {group.items.map((item) => (
                          <CheckRow
                            key={item.id}
                            item={item}
                            selectedStores={selectedStores}
                            preferredStoreId={preferredStoreId}
                            storesResolved={storesResolved}
                            likedBrandNames={likedBrandNames}
                            itemSystemProductIds={
                              productSystemProductIds.get(
                                item.householdproduct_id,
                              ) ?? EMPTY_IDS
                            }
                            onToggle={() => handleToggle(item)}
                            onSetQuantity={(qty) => handleSetQuantity(item, qty)}
                            onMarkUnavailable={() => handleMarkUnavailable(item)}
                            onPickStore={handlePickStore}
                            onPinDeal={handlePinDeal}
                            onRemoveOffer={handleRemoveOffer}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {purchasedItems.length > 0 && (
            <>
              <button
                type="button"
                style={s.purchasedToggleStyle}
                onClick={() => setShowPurchased((v) => !v)}
                data-testid="shop-purchased-toggle"
                aria-expanded={showPurchased}
              >
                {showPurchased
                  ? `Hide purchased (${purchasedItems.length})`
                  : `Show purchased (${purchasedItems.length})`}
              </button>
              {showPurchased && (
                <div
                  style={s.storeGroupStyle}
                  data-testid="shop-purchased-group"
                >
                  {purchasedItems.map((item) => (
                    <CheckRow
                      key={item.id}
                      item={item}
                      selectedStores={selectedStores}
                      preferredStoreId={preferredStoreId}
                      storesResolved={storesResolved}
                      likedBrandNames={likedBrandNames}
                      itemSystemProductIds={
                        productSystemProductIds.get(item.householdproduct_id) ??
                        EMPTY_IDS
                      }
                      onToggle={() => handleToggle(item)}
                      onSetQuantity={(qty) => handleSetQuantity(item, qty)}
                      onPickStore={handlePickStore}
                      onPinDeal={handlePinDeal}
                      onRemoveOffer={handleRemoveOffer}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {actionError && (
            <div role="alert" style={s.errorStyle} data-testid="shop-action-error">
              {actionError}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface CheckRowProps {
  item: ShoppingListItemWithDetails;
  /** The household's selected stores (id + name) — what the Where picker lists. */
  selectedStores: { id: string; name: string | null }[];
  /** The household's PREFERRED store id (`is_favorite`), pre-highlighted. */
  preferredStoreId?: string | null;
  /** True once the selected-store list query has resolved (vs still loading). */
  storesResolved: boolean;
  /** Liked brand NAMES (normalized) — the "· your brand" qualifier. */
  likedBrandNames: ReadonlySet<string>;
  /** This item's linked SystemProduct id(s) — identity match. */
  itemSystemProductIds: ReadonlySet<string>;
  /** Mark the item purchased / un-purchased — fired by the ✓ box only. */
  onToggle: () => void;
  /** Save a new quantity from the `qtyedit` quick tap-edit in the open view. */
  onSetQuantity: (quantity: number) => void;
  /**
   * Mark the item unavailable (sets `status: 'deferred'`). Present only on
   * pending rows — a purchased row has nothing to defer.
   */
  onMarkUnavailable?: () => void;
  /**
   * T27.2.7.9.3 — persist the where-to-buy pick (shared with Planning).
   * `(storeLocationId, retailerOfferId)`; re-tap clears with `(null, null)`.
   */
  onPickStore: (
    item: ShoppingListItemWithDetails,
    storeLocationId: string | null,
    retailerOfferId: string | null,
  ) => void;
  /**
   * T27.2.7.9.6 — pin a flyer (`deal`) or clear the pin (`null`), shared with
   * Planning. `retailerOfferId` is the deal's store's matched offer id (or null);
   * when pinning, the handler sets the chosen store + offer + pin in ONE write so
   * a flyer always belongs to its store (.9.6 reopen fix 2).
   */
  onPinDeal: (
    item: ShoppingListItemWithDetails,
    deal: StoreDealEntry | null,
    retailerOfferId: string | null,
  ) => void;
  /**
   * T27.2.7.9.6 — "Buy regular here": clear the pinned flyer + the carried offer
   * while keeping the selected store, so the compact row prices nothing.
   */
  onRemoveOffer: (item: ShoppingListItemWithDetails) => void;
}

function CheckRow({
  item,
  selectedStores,
  preferredStoreId,
  storesResolved,
  likedBrandNames,
  itemSystemProductIds,
  onToggle,
  onSetQuantity,
  onMarkUnavailable,
  onPickStore,
  onPinDeal,
  onRemoveOffer,
}: CheckRowProps): JSX.Element {
  // The row opens the expanded view; the ✓ box completes. The two never trigger
  // each other (prototype `#s-list-shop`): the box has its own tap with
  // stopPropagation, the row tap toggles `open` only.
  const [open, setOpen] = useState(false);
  // The Where picker is revealed on tap of the "Where" summary row (shared with
  // Planning). Collapsed by default so the panel reads note + Where + footer.
  const [showPicker, setShowPicker] = useState(false);

  // Per-store offers feeding the shared picker (the deal that rides along when a
  // chosen store has a matching offer). One always-mounted loader per selected
  // store pushes into this map; TanStack dedups the fetch across rows/modes.
  const [storeLocationOffers, setStoreLocationOffers] = useState<Record<string, StoreLocationOffers>>(
    {},
  );
  const handleResolved = useCallback((result: StoreLocationOffers) => {
    setStoreLocationOffers((prev) => {
      const existing = prev[result.storeId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.offers === result.offers
      ) {
        return prev;
      }
      return { ...prev, [result.storeId]: result };
    });
  }, []);
  const storesAllResolved =
    storesResolved &&
    selectedStores.length > 0 &&
    selectedStores.every((st) => {
      const r = storeLocationOffers[st.id];
      return r != null && !r.loading;
    });
  // The inline `qtyedit` on the collapsed `bsz` line: tapping `×N` reveals the
  // −/+ stepper in place (prototype `:1088`), tapping again collapses it.
  const [editingQty, setEditingQty] = useState(false);
  const done = item.status === 'purchased';
  const qty = Number(item.quantity) || 1;
  // The name line shows the item name ONLY — the brand lives on the `bsz`
  // secondary line below, not appended to the name (prototype `.nm` / `.bsz`).
  const name = item.display_title;
  const brand = item.offer_brand ?? null;
  const price =
    item.display_price != null ? Number(item.display_price) : null;

  // The per-trip note (`notes`) is read-only here — authored in Planning, read
  // in Shopping (the contract governs).
  const note = item.notes?.trim() || null;

  // T27.2.7.9.3 — the where-to-buy SUMMARY: the CHOSEN store's name (read
  // store-first via `storeOf` → `display_store` / `store_name`), else "Select
  // store". Tapping it reveals the shared picker below.
  const whereSummary =
    item.selected_storelocation_id != null
      ? storeOf(item) ?? 'Your store'
      : 'Select store';

  // Read-only package size — DISPLAY ONLY on the collapsed row. A tap reaches the
  // parent row-open button and opens the expanded view; the settings deep-link is
  // reachable only from the expanded view (PlanningItemRow's size link).
  // T36.3 — renders through the ONE shared formatter from the resolved measurement
  // snapshot (`4 L`, `12 × 20 g`), matching catalogue/expanded/Planning. No
  // fabricated unit: null when the product has no resolved measurement.
  const size = formatResolvedMeasurement({
    measurement_pack_count: item.product_measurement_pack_count ?? null,
    measurement_value: item.product_measurement_value ?? null,
    measurement_unit: item.product_measurement_unit ?? null,
    measurement_dimension: item.product_measurement_dimension ?? null,
    measurement_forms: item.product_measurement_forms ?? null,
  });

  // addedByByline — who added the item. Shares the contract names with Planning;
  // omit entirely when no adder is recorded.
  const addedByName = item.added_by_name?.trim() || null;
  const hasAddedBy = addedByName != null || item.added_by != null;
  const addedByText = addedByName ? `Added by ${addedByName}` : 'Added by you';
  // Relative "when added" suffix (today / yesterday / N days ago). Omitted when
  // `created_at` is missing or unparseable — never render `· Invalid Date`.
  const addedAt = addedAtRelative(item.created_at);

  // T12.1.8.7 — openable "added-from" deal. While the deal lives, expose a
  // "View deal" link; on the deal's purge the FK nulls and we omit the link (no
  // dead link). The link is a SIBLING of the row button, never nested inside it.
  // T27.2.7.9.4 — the link opens the deal IN PLACE (an overlay over the list,
  // the same Deals-page sheet), not a navigation to /deals; close returns here.
  const dealId = item.flyerdeal_id ?? null;
  // T27.2.7.9.6 — the open overlay deal id (the item's pinned `flyerdeal_id` for
  // the "View deal" link, OR a flyer the picker's "View" handed up).
  const [overlayDealId, setOverlayDealId] = useState<string | null>(null);

  return (
    <div style={s.checkRowWrapStyle}>
      {/* Always-mounted per-store loaders — render nothing, feed the shared
          Where picker's deal-rides-along matching. TanStack dedups the fetch
          across rows + modes. */}
      {selectedStores.map((st) => (
        <StoreLocationOffersLoader
          key={st.id}
          storeId={st.id}
          storeName={st.name}
          onResolved={handleResolved}
        />
      ))}
      <div style={s.checkRowStyle}>
        {/* The ✓ box — its OWN tap target (completes the item), with
            stopPropagation so it never opens the expanded view. */}
        <button
          type="button"
          style={s.boxButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-pressed={done}
          aria-label={`${done ? 'Uncheck' : 'Check off'} ${item.display_title}`}
          data-testid={`shop-item-${item.id}-box`}
        >
          <span style={done ? s.boxDoneStyle : s.boxStyle} aria-hidden="true">
            {done ? '✓' : ''}
          </span>
        </button>
        {/* The rest of the row opens the expanded view (never completes). */}
        <button
          type="button"
          style={s.checkRowOpenStyle}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`Open ${item.display_title}`}
          data-testid={`shop-item-${item.id}`}
        >
          <span
            style={s.thumbStyle}
            aria-hidden="true"
            data-testid={`shop-item-${item.id}-thumb`}
          >
            {shoppingListItemThumb(item)}
          </span>
          <span style={s.checkMetaStyle}>
            <span style={done ? s.checkNameDoneStyle : s.checkNameStyle}>
              {name}
            </span>
            {/* The `bsz` secondary line: brand · size · qtyedit ×N [· on sale]
                · notedot. The name above carries the item name only. */}
            <span style={done ? s.checkBszDoneStyle : s.checkBszStyle}>
              {brand && <span style={s.checkBrandStyle}>{brand}</span>}
              {brand && size && <span aria-hidden="true">·</span>}
              {size && (
                <span
                  style={s.sizeTextStyle}
                  data-testid={`shop-item-${item.id}-size`}
                >
                  {size}
                </span>
              )}
              {(brand || size) && <span aria-hidden="true">·</span>}
              {/* qtyedit — inline ×N tap-to-edit with a −/+ stepper. */}
              <span
                role="button"
                tabIndex={0}
                style={s.qtyEditStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingQty((v) => !v);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    setEditingQty((v) => !v);
                  }
                }}
                aria-label={`Quantity for ${item.display_title}: ${qty}`}
                data-testid={`shop-item-${item.id}-qtyedit`}
              >
                {editingQty ? (
                  <span
                    style={s.qStepStyle}
                    data-testid={`shop-item-${item.id}-qstep`}
                  >
                    {/* role="button" spans (not <button>) so they can nest inside
                        the row-open <button> — invalid HTML otherwise. */}
                    <span
                      role="button"
                      tabIndex={0}
                      style={s.qStepButtonStyle}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetQuantity(Math.max(1, qty - 1));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          onSetQuantity(Math.max(1, qty - 1));
                        }
                      }}
                      aria-label="Decrease quantity"
                      data-testid={`shop-item-${item.id}-qty-dec`}
                    >
                      −
                    </span>
                    <b style={s.qStepValueStyle}>{qty}</b>
                    <span
                      role="button"
                      tabIndex={0}
                      style={s.qStepButtonStyle}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetQuantity(qty + 1);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          onSetQuantity(qty + 1);
                        }
                      }}
                      aria-label="Increase quantity"
                      data-testid={`shop-item-${item.id}-qty-inc`}
                    >
                      +
                    </span>
                  </span>
                ) : (
                  <span style={s.qvStyle}>×{qty}</span>
                )}
              </span>
              {item.is_flyer_item && (
                <>
                  <span aria-hidden="true">·</span>
                  <span style={s.checkSaleStyle}>on sale</span>
                </>
              )}
              {note && (
                <span
                  style={s.noteDotStyle}
                  aria-label="Has a note"
                  data-testid={`shop-item-${item.id}-notedot`}
                />
              )}
            </span>
          </span>
          {price != null && (
            <span style={s.checkPriceStyle}>${price.toFixed(2)}</span>
          )}
        </button>
      </div>

      {open && (
        <div
          style={s.expandedViewStyle}
          data-testid={`shop-item-${item.id}-expanded`}
        >
          {/* itemNote — a BARE read-only field (no label), authored in Planning.
              Quantity moved to the collapsed `qtyedit`, so there is no "How
              many" row in the shopping panel (prototype `.shopdetail`). */}
          <span
            style={note ? s.noteReadonlyStyle : s.noteEmptyStyle}
            data-testid={`shop-item-${item.id}-note`}
          >
            {note ?? 'No note — add one in Planning'}
          </span>

          {/* T27.2.7.9.3 — Where-to-buy: the labelled summary row toggles the
              ONE shared picker (also used by Planning's row). Tapping reveals
              the household's selected stores; the deal rides along when the
              chosen store has a matching offer. */}
          <button
            type="button"
            style={planStyles.whereSummaryRowStyle}
            onClick={(e) => {
              e.stopPropagation();
              setShowPicker((v) => !v);
            }}
            aria-expanded={showPicker}
            data-testid={`shop-item-${item.id}-where`}
          >
            <span style={planStyles.whereSummaryLabelStyle}>Where</span>
            <span style={planStyles.whereSummaryValueStyle}>
              {whereSummary}
              <span style={planStyles.whereSummaryCaretStyle} aria-hidden="true">
                ▾
              </span>
            </span>
          </button>

          {showPicker && (
            <WhereToBuyPicker
              item={item}
              selectedStores={selectedStores}
              preferredStoreId={preferredStoreId}
              storeLocationOffersById={storeLocationOffers}
              storesResolved={storesAllResolved}
              likedBrandNames={likedBrandNames}
              itemSystemProductIds={itemSystemProductIds}
              onPick={(storeId, offerId) => onPickStore(item, storeId, offerId)}
              onPin={(deal, offerId) => onPinDeal(item, deal, offerId)}
              onRemoveOffer={() => onRemoveOffer(item)}
              onOpenDeal={setOverlayDealId}
              testidPrefix={`shop-item-${item.id}`}
            />
          )}

          {/* Mark-unavailable — a trailing action inside the expanded view
              (prototype `.sd-actions`). Sets the item to `deferred`, taking it
              out of the pending list when the shelf is empty. Pending rows
              only. */}
          {onMarkUnavailable && !done && (
            <div style={s.unavailableActionsStyle}>
              <button
                type="button"
                style={s.unavailableButtonStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkUnavailable();
                }}
                aria-label={`Mark ${item.display_title} unavailable`}
                data-testid={`shop-item-${item.id}-unavailable`}
              >
                Mark unavailable
              </button>
            </div>
          )}

          {/* itemFoot — the who-added byline (no fine-tune link in Shopping). */}
          <div style={s.itemFootStyle} data-testid={`shop-item-${item.id}-foot`}>
            {hasAddedBy && (
              <span
                style={s.addedByBylineStyle}
                data-testid={`shop-item-${item.id}-added-by`}
              >
                {addedAt ? `${addedByText} · ${addedAt}` : addedByText}
              </span>
            )}
          </div>
        </div>
      )}
      {dealId && (
        <button
          type="button"
          style={s.dealLinkStyle}
          onClick={(e) => {
            e.stopPropagation();
            setOverlayDealId(dealId);
          }}
          aria-label={`View the flyer deal for ${item.display_title}`}
          data-testid={`shop-item-${item.id}-view-deal`}
        >
          🏷️ View deal
        </button>
      )}

      {/* T27.2.7.9.4 — the in-place deal overlay (the same Deals-page sheet),
          mounted over the list. Opens the exact deal; closes back to the item.
          T27.2.7.9.6 — `overlayDealId` is the item's pinned deal (the link) OR a
          flyer the picker's "View" handed up. */}
      {overlayDealId && (
        <DealOverlay
          flyerDealId={overlayDealId}
          onClose={() => setOverlayDealId(null)}
        />
      )}
    </div>
  );
}

// Re-exported for callers that want the grouping logic in tests.
export { groupByStore, UNKNOWN_STORE_LABEL };
